import type { InspectionItem, Report, ServiceTransaction } from '../types';
import { eintraegeKollisionsfreiEinordnen, type TicketNummerWechsel } from './inspectionItemStatus';
// Blattmodul (nur Typen und flatRateLogic → serviceLogUtils): kein Zyklus.
import { istBestandteilBuchung } from './bestandteile/bestandteilLogic';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ÄNDERUNGEN SCHREIBEN STATT DEN GANZEN BLOB
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Offline-Warteschlange legt je (Nutzer, Projekt) den GANZEN `report` in
 * IndexedDB ab und schiebt ihn später hoch. Mobil liegt so ein Eintrag
 * Stunden. Beim Flush schreibt sie damit einen stundenalten Gesamtstand über
 * alles, was inzwischen passiert ist — am 31.08.2026 sind so 19 Buchungs-
 * zeilen aus fünf Projekten verschwunden (MIGRATIONS.md Nr. 64).
 *
 * Der Anker (projectVersionAnchor.ts) ERKENNT das jetzt. Dieses Modul sagt,
 * was danach zu tun ist: nicht den fremden Stand überschreiben und nicht die
 * eigene Feldarbeit wegwerfen, sondern beides zusammenführen — die eigenen
 * ÄNDERUNGEN auf den fremden Stand anwenden.
 *
 * ─── Drei Stände, und alle drei werden gebraucht ───────────────────────────
 *   base   — der Stand, auf dem der Warteschlangeneintrag AUFSETZT (beim
 *            ersten Einreihen festgehalten).
 *   mine   — der Stand, den der Eintrag schreiben will.
 *   theirs — was inzwischen auf dem Server steht.
 *
 * Ohne `base` ist eine Zusammenführung nicht möglich, nur eine Bergung: man
 * kann dann nicht unterscheiden, ob ein bei `mine` fehlender Wert gelöscht
 * oder nie gesehen wurde. Genau diese Unterscheidung ist der Kern — sie
 * trennt „ich habe diese Buchung zurückgenommen" von „ich kannte sie nicht".
 *
 * ─── Die Regeln, in dieser Reihenfolge ─────────────────────────────────────
 * 1. `mine` gleich `base`  → ich habe hier nichts geändert: `theirs` gilt.
 * 2. `theirs` gleich `base` → sonst hat niemand geändert: `mine` gilt.
 * 3. Beide geändert:
 *    a) Objekte werden Schlüssel für Schlüssel weiter zusammengeführt.
 *    b) SCHLÜSSELTRAGENDE LISTEN werden je Eintrag zusammengeführt: was ich
 *       hinzugefügt habe, kommt dazu; was der andere hinzugefügt hat, bleibt;
 *       entfernt wird eine Zeile nur, wenn ICH sie gegenüber `base` entfernt
 *       habe.
 *    c) Alles andere (Skalare, Listen ohne Schlüssel): `mine` gewinnt, und
 *       der Pfad wird als Konflikt GEMELDET. Nicht verschwiegen — ein
 *       Zusammenführer, der still entscheidet, ist die nächste Vorfallsklasse.
 *
 * ─── Warum schlüsseltragende Listen eine eigene Regel bekommen ─────────────
 * Eine Liste, deren Einträge eine `id` tragen, ist keine Aufzählung, sondern
 * ein Bestand: das Leistungsprotokoll, die Mängel, die Leistungen, die
 * Kontakte, die Prüflisten. An sie hängen mehrere Wege unabhängig an — eine
 * Buchung beim Versand, ein Mangel aus der Begehung, eine Position aus dem
 * LV-Import. Zwei Schreiber, die je einen Eintrag anhängen, haben keinen
 * Konflikt; sie haben zwei Einträge. Wer hier „mine gewinnt" anwendet, wirft
 * den fremden Eintrag weg.
 *
 * Und das wiegt schwerer, als es aussieht: `saveProjectToCloud` schreibt die
 * Kindtabellen mit PRUNE — was im Payload fehlt, wird gelöscht. Ein
 * veralteter Gesamtstand nimmt damit nicht nur Buchungszeilen mit, sondern
 * auch Mängel, Leistungen und Kontakte, die inzwischen entstanden sind. Für
 * die gibt es keinen Riegel wie Nr. 44; sie wären ersatzlos weg.
 *
 * ─── Wo diese Regel bewusst NICHT greift ───────────────────────────────────
 * Trägt eine Seite denselben Schlüssel zweimal, ist die Zuordnung nicht mehr
 * eindeutig — dann gilt die allgemeine Regel (mein Stand, Konflikt gemeldet).
 * Lieber ein gemeldeter Konflikt als eine stille Entdopplung, die einen
 * echten zweiten Eintrag verschluckt.
 */

/** Wo im Bericht das Leistungsprotokoll steht. */
const SERVICE_LOG_PATH = ['metadata', 'globalData', 'serviceLog'] as const;

/** Ein gemeldeter Konflikt: beide Seiten haben denselben Pfad geändert. */
export interface MergeConflict {
    /** Punktnotation, z.B. `metadata.projectName`. */
    path: string;
    /** Was auf dem Server steht — es wird VERWORFEN, deshalb steht es hier. */
    theirs: unknown;
    /** Was geschrieben wird. */
    mine: unknown;
}

export interface MergeResult<T> {
    merged: T;
    conflicts: MergeConflict[];
    /** Zeilen des Leistungsprotokolls, die nur der Server hatte und die erhalten blieben. */
    keptForeignLogEntries: number;
    /** Zeilen, die dieser Eintrag gegenüber seinem Ausgangsstand entfernt hat. */
    removedLogEntries: number;
    /** Neue Mängel dieses Eintrags, deren laufende Nummer schon vergeben war — siehe `neueMaengelEinordnen`. */
    ticketNummernWechsel: TicketNummerWechsel[];
    /**
     * Eigene automatische Positionsbuchungen (`BST-<serviceId>`), verworfen,
     * weil der Server-Stand dieselbe Position schon automatisch gebucht hat —
     * siehe `eigeneBstZeileVerwerfen`. Fehlt = 0.
     */
    verworfeneBstZeilen?: number;
}

/**
 * Die Referenz einer AUTOMATISCHEN Positionsbuchung (`BST-<serviceId>`,
 * Paketposition mit Bestandteilen), normalisiert — sonst `null`.
 */
const bstReferenz = (e: unknown): string | null => {
    const t = e as Pick<ServiceTransaction, 'bookingReference'> | null;
    return t && istBestandteilBuchung(t) ? String(t.bookingReference).trim().toLowerCase() : null;
};

/**
 * EINE automatische Positionsbuchung je Paketposition — auch beim Zusammenführen.
 *
 * ─── Der Fall (Prüfbefund F2, 19.10.2026) ───────────────────────────────────
 * Eine Sitzung bucht die Paketposition automatisch (`BST-<serviceId>`, Muster
 * B), ihr Flush hängt (offline, Build-Riegel). Nachts bucht der Nachtlauf
 * dieselbe Position — mit einer EIGENEN Zeile (andere id), weil er die der
 * Sitzung nie gesehen hat. Morgens flusht die Sitzung, der Anker trifft nicht,
 * und die Zusammenführung legt beide Zeilen nebeneinander: zwei verschiedene
 * ids, für die Liste zwei Einträge. Die DB-Wache (`bestandteil_buchung_guard`,
 * Nr. 166) weist das mit 23505 ab, die Warteschlange wiederholt endlos — und
 * mit ihr hängt jede andere Änderung dieses Eintrags.
 *
 * ─── Die Regel ──────────────────────────────────────────────────────────────
 * Trägt der fremde Stand (im Ergebnis) eine `BST-`-Zeile für dieselbe
 * Position, wird die EIGENE (andere id) verworfen. Der Server gewinnt: seine
 * Zeile ist geschrieben, sein Zähler steht, und die Menge der Buchung ist
 * absolut (Vertragsmenge) — die verworfene Zeile trug dieselbe Aussage. Den
 * Zähler übernimmt die Zusammenführung ohnehin vom Server (gemeinsame
 * Positions-id in der Schlüsselliste `services`: die fremde Fassung gilt).
 * Eine Zeile mit DERSELBEN id ist kein Fall dieser Regel — die ordnet die
 * Schlüsselliste ganz normal zu.
 */
const eigeneBstZeileVerwerfen = (fremdImErgebnis: readonly unknown[]): ((e: unknown) => boolean) => {
    const fremd = new Set(fremdImErgebnis.map(bstReferenz).filter((r): r is string => r !== null));
    return (e: unknown) => {
        const ref = bstReferenz(e);
        return ref !== null && fremd.has(ref);
    };
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Wertgleichheit über JSON-Struktur.
 *
 * Bewusst strukturell und nicht über `JSON.stringify`: dort entscheidet die
 * Schlüsselreihenfolge über das Ergebnis, und zwei inhaltsgleiche Stände aus
 * verschiedenen Codepfaden tragen ihre Schlüssel nicht in derselben Reihenfolge
 * — der Vergleich meldete dann Unterschiede, die keine sind, und aus jedem
 * zweiten Speichern würde ein Konflikt.
 */
export const deepEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;
    if (a === null || b === null || a === undefined || b === undefined) return false;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        return a.every((x, i) => deepEqual(x, b[i]));
    }
    if (isPlainObject(a) && isPlainObject(b)) {
        const ka = Object.keys(a), kb = Object.keys(b);
        if (ka.length !== kb.length) return false;
        return ka.every(k => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]));
    }
    return false;
};

/** Die Kennung einer Protokollzeile — ohne sie ist keine Zuordnung möglich. */
const logKey = (e: unknown): string | null => {
    const id = (e as { id?: unknown } | null)?.id;
    return typeof id === 'string' && id.trim() ? id.trim() : null;
};

/**
 * Die VORKOMMENS-Schlüssel einer Liste: `id`, und bei mehrfacher `id` `id#2`,
 * `id#3`, … in Reihenfolge des Auftretens.
 *
 * ─── Der Vorfall, aus dem das stammt (6240, 10.09.2026) ────────────────────
 * Vier Berichte (#11–#14, März/April) trugen im Protokoll DIESELBE `id` — ein
 * Client-Fehler von damals, den Nr. 60 beim Übernehmen ins Journal sauber als
 * vier Zeilen behandelt hat. `mergeServiceLog` hielt die id dagegen in einem
 * `seen`-Set und übernahm nur die erste: beim Versand von Bericht #37 fiel
 * Bericht #12 still aus dem Protokoll. Kein Riegel bemerkte es — 44/79 sehen
 * „id fehlt", und die id war ja noch da; 74 zählt (serviceId, reportRef), und
 * einen reportRef hatten die vier nie. Journal und Zähler behielten recht,
 * der Nachweis zeigte eine Begehung weniger.
 *
 * Der Kopf dieses Moduls sagt für Listen: „lieber gemeldeter Konflikt als
 * stille Entdopplung". Das Protokoll war davon ausgenommen. Jetzt gilt: eine
 * doppelte `id` ist KEIN Grund, eine Zeile zu verlieren — das n-te Vorkommen
 * derselben id wird als eigener Eintrag geführt und je Seite über seine
 * Ordnungszahl zugeordnet. Zwei Seiten, die dieselbe id je zweimal tragen,
 * ergeben zwei Zeilen; trägt eine Seite sie dreimal, bleiben drei. Verworfen
 * wird ein Vorkommen nur, wenn ICH es gegenüber dem Ausgangsstand entfernt
 * habe — genau wie bei jeder anderen Zeile.
 *
 * Das ist ausdrücklich keine Heilung der doppelten ids (die gehört in die
 * Daten: jede Dublette bekommt eine eigene uuid), sondern die Zusicherung,
 * dass der Merge sie nicht verschlimmert.
 */
const vorkommensSchluessel = (list: readonly unknown[]): (string | null)[] => {
    const zaehler = new Map<string, number>();
    return list.map(e => {
        const id = logKey(e);
        if (!id) return null;
        const n = (zaehler.get(id) ?? 0) + 1;
        zaehler.set(id, n);
        return n === 1 ? id : `${id}#${n}`;
    });
};

/**
 * Das Leistungsprotokoll zusammenführen.
 *
 * Zeilen OHNE Kennung lassen sich keiner Seite zuordnen; sie werden aus BEIDEN
 * Ständen übernommen und dabei nur gegen exakte Inhaltsgleichheit entdoppelt.
 * Sie zu verwerfen wäre Datenverlust, sie über die Arrayposition zu
 * identifizieren wäre falsch (jeder Schreibpfad hängt an einer anderen Stelle
 * an — dieselbe Position bezeichnet nach einem Anhängen eine andere Buchung).
 */
export const mergeServiceLog = (
    base: ServiceTransaction[],
    mine: ServiceTransaction[],
    theirs: ServiceTransaction[],
): { merged: ServiceTransaction[]; kept: number; removed: number; verworfen: number } => {
    // Vorkommens-Schlüssel statt roher id: eine doppelte id verliert so keine
    // Zeile (siehe `vorkommensSchluessel`).
    const baseKeys = vorkommensSchluessel(base);
    const mineKeys = vorkommensSchluessel(mine);
    const theirsKeys = vorkommensSchluessel(theirs);
    const baseIds = new Set(baseKeys.filter(Boolean) as string[]);
    const mineIds = new Set(mineKeys.filter(Boolean) as string[]);

    // Was ICH gegenüber meinem Ausgangsstand entfernt habe — und nur das darf
    // aus dem fremden Stand verschwinden.
    const removedByMe = new Set([...baseIds].filter(id => !mineIds.has(id)));

    const merged: ServiceTransaction[] = [];
    const seen = new Set<string>();
    let kept = 0;

    // Zuerst der fremde Stand: er ist der neuere, und seine Reihenfolge ist
    // die, die alle anderen Leser gerade sehen.
    theirs.forEach((e, i) => {
        const id = theirsKeys[i];
        if (id && removedByMe.has(id)) return;
        if (id) { if (seen.has(id)) return; seen.add(id); }
        merged.push(e);
        if (id && !mineIds.has(id)) kept++;
    });

    // Bis hier steht nur der fremde Stand im Ergebnis: seine BST-Zeilen sind
    // die, gegen die eine eigene verworfen wird (eigeneBstZeileVerwerfen).
    const verwerfen = eigeneBstZeileVerwerfen(merged);
    let verworfen = 0;

    mine.forEach((e, i) => {
        const id = mineKeys[i];
        if (id) {
            if (seen.has(id)) return;
            seen.add(id);
            if (verwerfen(e)) { verworfen++; return; }
            merged.push(e);
            return;
        }
        // Ohne Kennung: nur übernehmen, wenn keine inhaltsgleiche Zeile steht.
        if (!merged.some(x => logKey(x) === null && deepEqual(x, e))) merged.push(e);
    });

    return { merged, kept, removed: removedByMe.size, verworfen };
};

/**
 * Ist das eine Liste, deren Einträge sich über ihre `id` zuordnen lassen?
 *
 * Bedingung für BEIDE Seiten: eine Liste, jeder Eintrag ein Objekt mit einer
 * nicht-leeren `id`, und keine `id` doppelt. Eine leere Liste zählt mit — sie
 * widerspricht der Zuordnung nicht. Der Ausgangsstand muss die Bedingung nicht
 * erfüllen; er liefert nur, was entfernt wurde.
 */
const istSchluesselliste = (mine: unknown, theirs: unknown, base: unknown): boolean => {
    const passt = (v: unknown): boolean => {
        if (!Array.isArray(v)) return false;
        const ids: string[] = [];
        for (const e of v) {
            if (!isPlainObject(e)) return false;
            const id = logKey(e);
            if (!id) return false;
            ids.push(id);
        }
        return new Set(ids).size === ids.length;
    };
    if (!passt(mine) || !passt(theirs)) return false;
    // Beide leer wäre eine Liste ohne jede Aussage — sie ist oben schon als
    // gleich erkannt und kommt hier gar nicht an.
    if (base !== undefined && base !== null && !Array.isArray(base)) return false;
    return true;
};

interface Zustand {
    conflicts: MergeConflict[];
    kept: number;
    removed: number;
    verworfen: number;
}

const mergeNode = (
    base: unknown,
    mine: unknown,
    theirs: unknown,
    path: string[],
    z: Zustand,
): unknown => {
    if (deepEqual(mine, theirs)) return mine;
    // Regel 1 und 2 — nur eine Seite hat geändert.
    if (deepEqual(mine, base)) return theirs;
    if (deepEqual(theirs, base)) return mine;

    // Schlüsseltragende Listen — das Leistungsprotokoll immer, jede andere
    // Liste, deren Einträge eine eindeutige `id` tragen.
    const istProtokoll = path.length === SERVICE_LOG_PATH.length
        && path.every((p, i) => p === SERVICE_LOG_PATH[i]);
    if (istProtokoll || istSchluesselliste(mine, theirs, base)) {
        const arr = (v: unknown): ServiceTransaction[] => (Array.isArray(v) ? v as ServiceTransaction[] : []);
        const r = mergeServiceLog(arr(base), arr(mine), arr(theirs));
        if (istProtokoll) { z.kept += r.kept; z.removed += r.removed; z.verworfen += r.verworfen; }
        return r.merged;
    }

    // Objekte Schlüssel für Schlüssel.
    if (isPlainObject(mine) && isPlainObject(theirs)) {
        const b = isPlainObject(base) ? base : {};
        const out: Record<string, unknown> = {};
        for (const key of new Set([...Object.keys(theirs), ...Object.keys(mine)])) {
            const hatMine = Object.prototype.hasOwnProperty.call(mine, key);
            const hatTheirs = Object.prototype.hasOwnProperty.call(theirs, key);
            const hatBase = Object.prototype.hasOwnProperty.call(b, key);
            // Ein Schlüssel, den nur EINE Seite kennt: hat ihn der
            // Ausgangsstand nicht, ist er neu und kommt mit. Hat er ihn, hat
            // die andere Seite ihn entfernt — dann bleibt er entfernt.
            if (!hatMine) { if (!hatBase) out[key] = theirs[key]; continue; }
            if (!hatTheirs) { if (!hatBase) out[key] = mine[key]; continue; }
            const wert = mergeNode(b[key], mine[key], theirs[key], [...path, key], z);
            if (wert !== undefined || mine[key] === undefined) out[key] = wert;
        }
        return out;
    }

    // Alles Übrige: mein Stand gewinnt, der Pfad wird gemeldet.
    z.conflicts.push({ path: path.join('.') || '(wurzel)', theirs, mine });
    return mine;
};

/**
 * Die eigenen Änderungen auf den fremden Stand anwenden.
 *
 * `theirs` ist der Ausgangspunkt des Ergebnisses, NICHT `mine`: was dieser
 * Eintrag nie gesehen hat, bleibt damit erhalten, auch wenn niemand daran
 * gedacht hat, es in die Regeln aufzunehmen. Der umgekehrte Aufbau — von
 * `mine` ausgehen und Fremdes hineinholen — verliert genau das, woran niemand
 * gedacht hat, und das ist der Fehler, der hier behoben wird.
 */
export const mergeReports = (base: Report, mine: Report, theirs: Report): MergeResult<Report> => {
    const z: Zustand = { conflicts: [], kept: 0, removed: 0, verworfen: 0 };
    const zusammen = mergeNode(base, mine, theirs, [], z) as Report;
    const { merged, wechsel } = neueMaengelEinordnen(base, mine, theirs, zusammen);
    return {
        merged,
        conflicts: z.conflicts,
        keptForeignLogEntries: z.kept,
        removedLogEntries: z.removed,
        ticketNummernWechsel: wechsel,
        ...(z.verworfen > 0 ? { verworfeneBstZeilen: z.verworfen } : {}),
    };
};

/**
 * Neue Mängel dieses Eintrags kollisionsfrei nummerieren.
 *
 * ─── Der Vorfall (6051, 29.09.2026) ────────────────────────────────────────
 * Ein Tab mit altem Build hielt acht Feststellungen der Begehung #149 mit
 * #759–#766, das Speichern scheiterte, die Warteschlange behielt sie. Am
 * nächsten Tag vergab der Serverstand (höchste Nummer 758) dieselben Nummern
 * an acht neue Mängel. Beim Nachspielen hängte die Schlüsselliste Rolfs
 * Einträge über ihre `id` an — ohne Blick auf die Nummer: acht Nummern doppelt,
 * so auch im versendeten Bericht #150.
 *
 * Die Nummer entsteht beim Einfügen aus dem LOKALEN Bestand
 * (`neuenEintragEinfuegen`); zwei Stände, die unabhängig voneinander einfügen,
 * vergeben zwangsläufig dieselben. Aufgelöst wird deshalb hier, wo beide
 * Stände zusammenkommen — nach derselben Regel wie beim Wiederherstellen:
 * der HEREINKOMMENDE weicht aus (`eintraegeKollisionsfreiEinordnen`). Was auf
 * dem Server steht, haben andere schon gesehen und womöglich versendet.
 *
 * Neu heißt: nur in `mine`, weder im Ausgangsstand noch im fremden Stand. Ein
 * Eintrag, den der fremde Stand kennt, kommt ohnehin als dessen Fassung in
 * die Liste (mergeServiceLog nimmt bei gemeinsamer `id` den fremden Eintrag).
 * Die Neuen werden in der Reihenfolge ihrer bisherigen Nummer eingeordnet,
 * damit ihre Abfolge erhalten bleibt — die Liste selbst steht neueste zuerst.
 */
const neueMaengelEinordnen = (
    base: Report,
    mine: Report,
    theirs: Report,
    merged: Report,
): { merged: Report; wechsel: TicketNummerWechsel[] } => {
    const liste = merged?.items;
    if (!Array.isArray(liste) || !Array.isArray(mine?.items)) return { merged, wechsel: [] };

    const bekannt = new Set<string>();
    for (const quelle of [base?.items, theirs?.items]) {
        if (!Array.isArray(quelle)) continue;
        for (const e of quelle) {
            const id = logKey(e);
            if (id) bekannt.add(id);
        }
    }
    const neuIds = new Set<string>();
    for (const e of mine.items) {
        const id = logKey(e);
        if (id && !bekannt.has(id)) neuIds.add(id);
    }
    if (neuIds.size === 0) return { merged, wechsel: [] };

    const istNeu = (e: InspectionItem): boolean => neuIds.has(logKey(e) ?? '');
    const neu = liste.filter(istNeu)
        .map((e, i) => ({ e, i }))
        .sort((a, b) => ((a.e.ticketNumber ?? Infinity) - (b.e.ticketNumber ?? Infinity)) || a.i - b.i)
        .map(x => x.e);
    const { eintraege, umnummeriert } = eintraegeKollisionsfreiEinordnen(neu, liste.filter(e => !istNeu(e)));

    const ersatz = new Map<string, InspectionItem>();
    neu.forEach((alt, i) => { if (eintraege[i] !== alt) ersatz.set(alt.id, eintraege[i]); });
    if (ersatz.size === 0) return { merged, wechsel: [] };
    return {
        merged: { ...merged, items: liste.map(e => ersatz.get(e.id) ?? e) },
        wechsel: umnummeriert,
    };
};

/** Die drei Felder einer Position, die aus Buchung und Abrechnung entstehen. */
export const ZAEHLER_FELDER = ['quantityUsed', 'quantityBilled', 'status'] as const;

/**
 * Die Zähler der Positionen aus dem fremden Stand übernehmen — je Position
 * über ihre `id`, und nur dort, wo der fremde Stand die Position kennt.
 *
 * Warum bei der Bergung überhaupt: `mine` hat keinen Ausgangsstand, und sein
 * Zähler ist genau so alt wie der Rest. Das Protokoll wird oben gerettet, der
 * Zähler kam bisher trotzdem aus dem alten Stand mit — am 07.09.2026 stand
 * so in 24 Projekten Protokoll N neben quantity_used N-1. Die Zusammenführung
 * MIT Ausgangsstand (mergeReports) übernimmt bei einer gemeinsamen `id`
 * ohnehin den fremden Eintrag ganz; die Bergung zieht hier nach, damit der
 * gemeldete `savedReport` dieselben Zähler trägt, die der Server tatsächlich
 * behält (der Schreibweg lässt sie mit `counters: 'keep-server'` weg).
 *
 * Grenze: ein Stand, dessen `services` leer sind, weiß über Zähler nichts —
 * der JSON-Blob leert `services` vor dem Speichern, die Positionen liegen
 * relational. Dann bleibt alles wie es ist; die Auslassung im Schreibweg
 * ist die eigentliche Sicherung, das hier ist nur die Sicht darauf.
 *
 * Liefert `null`, wenn nichts zu übernehmen ist (kein Kopieren im Normalfall).
 */
const zaehlerVomServerStand = (mine: Report, theirs: Report): unknown[] | null => {
    const mineServices = mine?.metadata?.globalData?.services;
    const theirsServices = theirs?.metadata?.globalData?.services;
    if (!Array.isArray(mineServices) || !Array.isArray(theirsServices) || theirsServices.length === 0) return null;

    const fremd = new Map<string, Record<string, unknown>>();
    for (const s of theirsServices) {
        const id = logKey(s);
        if (id && isPlainObject(s)) fremd.set(id, s);
    }

    let geaendert = false;
    const out = mineServices.map(s => {
        const id = logKey(s);
        const f = id ? fremd.get(id) : undefined;
        if (!f || !isPlainObject(s)) return s;
        const kopie: Record<string, unknown> = { ...s };
        let hier = false;
        for (const feld of ZAEHLER_FELDER) {
            if (!Object.prototype.hasOwnProperty.call(f, feld)) continue;
            if (deepEqual(kopie[feld], f[feld])) continue;
            kopie[feld] = f[feld];
            hier = true;
        }
        if (hier) geaendert = true;
        return hier ? kopie : s;
    });
    return geaendert ? out : null;
};

/**
 * DIE BERGUNG — für Einträge ohne Ausgangsstand.
 *
 * Warteschlangeneinträge, die vor dieser Version entstanden sind, tragen
 * keinen `base`. Für sie ist eine Zusammenführung unmöglich (siehe oben), und
 * sie einfach zu verwerfen hieße, die Feldarbeit eines Mitarbeiters
 * wegzuwerfen. Also wird gerettet, was nachweislich verloren ginge: jede
 * Protokollzeile des Serverstands, die der Eintrag nicht kennt, wird an sein
 * Protokoll ANGEHÄNGT. Ohne Ausgangsstand kann keine Zeile als „von mir
 * entfernt" gelten — es wird deshalb NICHTS entfernt.
 *
 * Das ist ausdrücklich weniger als eine Zusammenführung: alles außerhalb des
 * Protokolls (Notizen, Metadaten, Berichtsinhalte) wird weiterhin vom älteren
 * Stand überschrieben. Es ist die Übergangsregel für Alteinträge, kein
 * Dauerzustand.
 *
 * Seit dem 07.09.2026 eine zweite Ausnahme: die ZÄHLER der Positionen
 * (`quantityUsed`, `quantityBilled`, `status`) kommen je `id` aus dem fremden
 * Stand, sofern der sie kennt — siehe `zaehlerVomServerStand`.
 *
 * Seit dem 19.10.2026 eine dritte: eine eigene `BST-`-Zeile weicht der des
 * Servers für dieselbe Position (eigeneBstZeileVerwerfen).
 */
export const salvageForeignLogEntries = (mine: Report, theirs: Report): MergeResult<Report> => {
    const mineLog = (mine?.metadata?.globalData?.serviceLog ?? []) as ServiceTransaction[];
    const theirsLog = (theirs?.metadata?.globalData?.serviceLog ?? []) as ServiceTransaction[];
    // Vorkommens-Schlüssel wie in mergeServiceLog: kennt der Server eine id
    // dreimal und ich sie zweimal, fehlt mir das dritte Vorkommen.
    const mineIds = new Set(vorkommensSchluessel(mineLog).filter(Boolean) as string[]);
    const theirsKeys = vorkommensSchluessel(theirsLog);

    const fehlend = theirsLog.filter((e, i) => {
        const id = theirsKeys[i];
        if (id) return !mineIds.has(id);
        return !mineLog.some(x => logKey(x) === null && deepEqual(x, e));
    });
    // Die einzige Zeile, die die Bergung aus dem EIGENEN Protokoll nimmt: eine
    // eigene automatische Positionsbuchung, wenn der Server dieselbe Position
    // schon unter anderer id gebucht hat (eigeneBstZeileVerwerfen). Sonst
    // stuenden zwei BST-Zeilen da, und die DB-Wache wiese jeden Flush ab.
    const theirsIds = new Set(theirsKeys.filter(Boolean) as string[]);
    const mineKeys = vorkommensSchluessel(mineLog);
    const verwerfen = eigeneBstZeileVerwerfen(theirsLog);
    const eigeneBleiben = mineLog.filter((e, i) => {
        const k = mineKeys[i];
        return (k !== null && theirsIds.has(k)) || !verwerfen(e);
    });
    const verworfen = mineLog.length - eigeneBleiben.length;
    const services = zaehlerVomServerStand(mine, theirs);
    if (fehlend.length === 0 && !services && verworfen === 0) {
        return { merged: mine, conflicts: [], keptForeignLogEntries: 0, removedLogEntries: 0, ticketNummernWechsel: [] };
    }

    const merged: Report = {
        ...mine,
        metadata: {
            ...mine.metadata,
            globalData: {
                ...(mine.metadata?.globalData ?? {} as any),
                serviceLog: fehlend.length === 0 && verworfen === 0 ? mineLog : [...fehlend, ...eigeneBleiben],
                ...(services ? { services } : {}),
            },
        },
    } as Report;

    return {
        merged,
        conflicts: [],
        keptForeignLogEntries: fehlend.length,
        removedLogEntries: 0,
        ticketNummernWechsel: [],
        ...(verworfen > 0 ? { verworfeneBstZeilen: verworfen } : {}),
    };
};

/**
 * Die Einträge des fremden Stands, die der eigene nicht kennt, hinten
 * anhängen — über ihre `id`. Was beide kennen, bleibt in der eigenen
 * Fassung (die Zähler der Positionen hat `zaehlerVomServerStand` schon
 * geholt). `null`, wenn nichts fehlt.
 */
const fehlendeNachId = (mine: unknown, theirs: unknown): { liste: unknown[]; neu: number } | null => {
    if (!Array.isArray(theirs) || theirs.length === 0) return null;
    const eigene = Array.isArray(mine) ? mine : [];
    const bekannt = new Set(eigene.map(logKey).filter((id): id is string => id !== null));
    const fehlend = theirs.filter(e => {
        const id = logKey(e);
        return id !== null && !bekannt.has(id);
    });
    return fehlend.length > 0 ? { liste: [...eigene, ...fehlend], neu: fehlend.length } : null;
};

/** Die Bergung beim Öffnen — `salvageForeignLogEntries` plus Positionen und Notizen des Servers. */
export interface EntwurfsBergung extends MergeResult<Report> {
    /** Positionen und Notizen, die nur der Server führte und die in den Entwurf kamen. */
    uebernommeneServerZeilen: number;
}

/**
 * DIE BERGUNG BEIM ÖFFNEN — ein Eintrag ohne Ausgangsstand wird als Entwurf
 * auf den Serverstand gelegt.
 *
 * Wie `salvageForeignLogEntries`, dazu die Positionen (`services`) und
 * Notizen (`notes`), die der Server führt und der Eintrag nicht: sie kommen
 * per `id` in den Entwurf. Ohne Ausgangsstand kann keine fehlende Zeile als
 * „von mir entfernt" gelten, nur als „nie gesehen" — dieselbe Regel wie beim
 * Protokoll.
 *
 * ─── Warum beim Öffnen und nicht nur beim Speichern (Lücke O1, 21.10.2026) ─
 * Der Speicherweg schützt die Kindtabellen bei der Bergung schon selbst
 * (`kindZeilen: 'nur-ergaenzen'`). Beim Öffnen aber wird der Ausgangsstand
 * zum frischen Serverstand und der Anker zum frischen `updated_at` — der
 * nächste Autosave, Versand oder Rückweg trifft den Anker und gleicht die
 * Kindtabellen ohne Bergung gegen den Entwurf ab. Was dort fehlt, wird
 * gelöscht: in 6185 zwei abgerechnete Positionen und drei Notizen. Steht
 * im Entwurf, was der Server führt, hat der Abgleich nichts zu löschen.
 *
 * Bewusst nur Positionen und Notizen, wie beim Speicherweg: dort sitzen Geld
 * und Nachweis. Eine absichtliche Löschung im alten Stand kommt damit
 * zurück — sichtbar und korrigierbar, anders als eine still gelöschte,
 * abgerechnete Position.
 */
export const bergeEntwurfAufServerstand = (mine: Report, theirs: Report): EntwurfsBergung => {
    const geborgen = salvageForeignLogEntries(mine, theirs);
    const gd = geborgen.merged?.metadata?.globalData;
    const services = fehlendeNachId(gd?.services, theirs?.metadata?.globalData?.services);
    const notes = fehlendeNachId(gd?.notes, theirs?.metadata?.globalData?.notes);
    if (!services && !notes) return { ...geborgen, uebernommeneServerZeilen: 0 };
    return {
        ...geborgen,
        merged: {
            ...geborgen.merged,
            metadata: {
                ...geborgen.merged.metadata,
                globalData: {
                    ...gd,
                    ...(services ? { services: services.liste } : {}),
                    ...(notes ? { notes: notes.liste } : {}),
                },
            },
        } as Report,
        uebernommeneServerZeilen: (services?.neu ?? 0) + (notes?.neu ?? 0),
    };
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NACH DEM FLUSH: DEN GESCHRIEBENEN STAND IN DEN ARBEITSSTAND ÜBERNEHMEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis zum 08.09.2026 endete die Zusammenführung am Schreibweg: der Server
 * bekam das zusammengeführte Protokoll und behielt seine Zähler, der
 * In-Memory-Report der App aber blieb, wie er war — ohne die fremde Zeile,
 * mit dem alten Zähler. Der nächste Autosave reihte genau diesen Stand mit
 * dem eben geschriebenen als Ausgangsstand ein, der Anker traf, der
 * Normalpfad schrieb den Vollblob, und der DB-Riegel holte die Zeile und den
 * Zähler jedes Mal zurück. Projekt 26051016: 43 Rettungen derselben Zeile
 * 6bb1abd5 in 23 Minuten, weil Markus Meyer mobil vorbereitet und am PC
 * weitergearbeitet hat.
 *
 * Deshalb hier die Gegenrichtung zu `mergeReports`: nicht die eigenen
 * Änderungen auf den fremden Stand, sondern den GESCHRIEBENEN Stand in den
 * eigenen — und zwar nur dort, wo er mehr weiß: Protokollzeilen, die er
 * allein hat, und Zähler, die der Server hält. Alles Übrige bleibt der
 * eigene Stand; wer nach dem Flush weitergetippt hat, verliert nichts.
 *
 *   base   — Ausgangsstand des geflushten Eintrags (undefined bei Alteinträgen).
 *   mine   — der Arbeitsstand der App, möglicherweise schon weiter als der Flush.
 *   theirs — was der Server nach dem Flush tatsächlich hält (Antwortzeile,
 *            Protokoll NACH den Triggern, Zähler des geschriebenen Reports).
 */
export interface StandUebernahme {
    /** `=== mine`, wenn nichts zu übernehmen war — React rendert dann nichts neu. */
    merged: Report;
    /** Protokollzeilen, die nur der geschriebene Stand hatte. */
    keptForeignLogEntries: number;
    /** Positionen, bei denen mindestens ein Zählerfeld vom geschriebenen Stand kam. */
    zaehlerUebernommen: number;
}

/**
 * Die Zähler wie in `zaehlerVomServerStand`, mit einer Ausnahme: hat der
 * Server ein Feld gegenüber dem Ausgangsstand NICHT verändert und ich danach
 * schon (theirs gleich base, mine anders), ist mein Wert der neuere und
 * bleibt. Ohne Ausgangsstand gibt es diese Unterscheidung nicht — dann hält
 * der Server die Wahrheit, wie bei der Bergung.
 */
const zaehlerVomGeschriebenenStand = (
    base: Report | null | undefined,
    mine: Report,
    theirs: Report,
): { services: unknown[]; positionen: number } | null => {
    const mineServices = mine?.metadata?.globalData?.services;
    const theirsServices = theirs?.metadata?.globalData?.services;
    if (!Array.isArray(mineServices) || !Array.isArray(theirsServices) || theirsServices.length === 0) return null;

    const nachId = (liste: unknown): Map<string, Record<string, unknown>> => {
        const m = new Map<string, Record<string, unknown>>();
        if (!Array.isArray(liste)) return m;
        for (const s of liste) {
            const id = logKey(s);
            if (id && isPlainObject(s)) m.set(id, s);
        }
        return m;
    };
    const fremd = nachId(theirsServices);
    const ausgang = nachId(base?.metadata?.globalData?.services);

    let positionen = 0;
    const out = mineServices.map(s => {
        const id = logKey(s);
        const f = id ? fremd.get(id) : undefined;
        if (!f || !isPlainObject(s)) return s;
        const b = id ? ausgang.get(id) : undefined;
        const kopie: Record<string, unknown> = { ...s };
        let hier = false;
        for (const feld of ZAEHLER_FELDER) {
            if (!Object.prototype.hasOwnProperty.call(f, feld)) continue;
            if (deepEqual(kopie[feld], f[feld])) continue;
            // Server unverändert gegenüber dem Ausgangsstand, ich danach
            // weiter: meins ist das Neuere.
            if (b && deepEqual(f[feld], b[feld])) continue;
            kopie[feld] = f[feld];
            hier = true;
        }
        if (hier) positionen++;
        return hier ? kopie : s;
    });
    return positionen > 0 ? { services: out, positionen } : null;
};

/**
 * Wie die JSON-Rundreise: Schlüssel mit `undefined` fallen weg, rekursiv.
 * Nur zum Vergleichen — der Wert wird nie geschrieben.
 */
export const ohneUndefined = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(ohneUndefined);
    if (isPlainObject(v)) {
        const out: Record<string, unknown> = {};
        for (const [k, w] of Object.entries(v)) {
            if (w !== undefined) out[k] = ohneUndefined(w);
        }
        return out;
    }
    return v;
};

/**
 * Nach einem bestätigten Flush: den GESCHRIEBENEN Stand (theirs) in den
 * In-Memory-Report (mine) übernehmen, ohne neuere lokale Arbeit zu verlieren.
 * base = Ausgangsstand des geflushten Eintrags (undefined bei Alteinträgen).
 *
 * Protokoll: `mergeServiceLog(base, mine, theirs)` — fremde Zeilen kommen
 * dazu; was ich gegenüber dem Ausgangsstand entfernt habe (auch erst nach dem
 * Flush), bleibt weg; was ich nach dem Flush angehängt habe, bleibt.
 * Zähler: je Position über `id`, siehe `zaehlerVomGeschriebenenStand`.
 * Positionen, die nur der Server kennt, kommen NICHT dazu — sie liegen
 * relational, das ist nicht Aufgabe dieser Funktion.
 */
export const uebernimmGeschriebenenStand = (
    base: Report | null | undefined,
    mine: Report,
    theirs: Report,
): StandUebernahme => {
    const mineLog = (mine?.metadata?.globalData?.serviceLog ?? []) as ServiceTransaction[];
    const theirsLog = (theirs?.metadata?.globalData?.serviceLog ?? []) as ServiceTransaction[];
    const baseLog = (base?.metadata?.globalData?.serviceLog ?? []) as ServiceTransaction[];

    const protokoll = mergeServiceLog(baseLog, mineLog, theirsLog);
    // Verglichen wird nach der JSON-Rundreise: `theirs` kommt aus der
    // Antwortzeile der Datenbank, und dort sind `undefined`-Felder weg, die
    // der Arbeitsstand noch traegt. Ohne diese Angleichung meldete jeder
    // Flush eine „Aenderung", der State wuerde jedes Mal neu gesetzt — ein
    // Render mehr und eine Meldung, die nichts sagt.
    const protokollGeaendert = !deepEqual(ohneUndefined(protokoll.merged), ohneUndefined(mineLog));
    const zaehler = zaehlerVomGeschriebenenStand(base, mine, theirs);

    if (!protokollGeaendert && !zaehler) {
        return { merged: mine, keptForeignLogEntries: 0, zaehlerUebernommen: 0 };
    }

    const merged: Report = {
        ...mine,
        metadata: {
            ...mine.metadata,
            globalData: {
                ...(mine.metadata?.globalData ?? {} as any),
                ...(protokollGeaendert ? { serviceLog: protokoll.merged } : {}),
                ...(zaehler ? { services: zaehler.services } : {}),
            },
        },
    } as Report;

    return {
        merged,
        keptForeignLogEntries: protokoll.kept,
        zaehlerUebernommen: zaehler?.positionen ?? 0,
    };
};
